import { useId } from 'react';
import { useIntl } from 'react-intl';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import type { ActivitySummaryBreakdown } from './activity-summary';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import { alpha, Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';

export type ActivityBreakdownSwitcherProps = {
  value: ActivitySummaryBreakdown;
  onChange: (value: ActivitySummaryBreakdown) => void;
};

const ActivityBreakdownSwitcher = ({ value, onChange }: ActivityBreakdownSwitcherProps) => {
  const intl = useIntl();
  const labelId = useId();
  const label = intl.formatMessage({
    id: 'activityMonitoring.breakdown.label',
    defaultMessage: 'Detay kırılımı',
  });
  const options: Array<{
    value: ActivitySummaryBreakdown;
    label: string;
    icon: JSX.Element;
  }> = [
    {
      value: 'user',
      label: intl.formatMessage({
        id: 'activityMonitoring.breakdown.user',
        defaultMessage: 'Kullanıcı',
      }),
      icon: <PersonRoundedIcon fontSize="small" />,
    },
    {
      value: 'project',
      label: intl.formatMessage({
        id: 'activityMonitoring.breakdown.project',
        defaultMessage: 'Proje',
      }),
      icon: <BusinessRoundedIcon fontSize="small" />,
    },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 0.75,
        width: { xs: '100%', sm: 'auto' },
      }}>
      <Typography
        id={labelId}
        component="span"
        variant="caption"
        color="text.secondary"
        sx={{ flexShrink: 0, fontWeight: 700 }}>
        {label}
      </Typography>

      <ToggleButtonGroup
        exclusive
        size="small"
        value={value}
        aria-labelledby={labelId}
        onChange={(_, nextValue: ActivitySummaryBreakdown | null) => {
          if (nextValue) onChange(nextValue);
        }}
        sx={{
          width: { xs: '100%', sm: 'auto' },
          p: 0.35,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          backgroundColor: 'background.default',
          boxShadow: 'none',
          '& .MuiToggleButtonGroup-grouped': {
            flex: { xs: 1, sm: '0 0 auto' },
            minHeight: 34,
            minWidth: { xs: 0, sm: 104 },
            px: 1.25,
            gap: 0.625,
            border: 0,
            borderRadius: '6px !important',
            color: 'text.secondary',
            fontSize: '0.78rem',
            fontWeight: 700,
            lineHeight: 1.2,
            textTransform: 'none',
            whiteSpace: 'nowrap',
            boxShadow: 'none',
            '&.Mui-selected': {
              color: (theme) =>
                theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.dark,
              backgroundColor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.2 : 0.11),
              boxShadow: (theme) => theme.shadows[1],
            },
            '&.Mui-selected:hover': {
              backgroundColor: (theme) =>
                alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.26 : 0.16),
            },
            '&.Mui-focusVisible': {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: 1,
            },
          },
        }}>
        {options.map((option) => (
          <ToggleButton key={option.value} value={option.value} aria-label={option.label}>
            {option.icon}
            {option.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
};

export default ActivityBreakdownSwitcher;
